import React, { useState } from "react";
import { AlertTriangle, CreditCard, XCircle, Clock, CheckCircle2 } from "lucide-react";

interface SubscriptionStatusBannerProps {
  status: "active" | "past_due" | "canceled" | "incomplete" | "trialing";
  plan: "free" | "premium";
  onCompletePayment?: () => void;
  onUpdatePaymentMethod?: () => void;
  loading?: boolean;
}

export const SubscriptionStatusBanner: React.FC<SubscriptionStatusBannerProps> = ({
  status,
  plan,
  onCompletePayment,
  onUpdatePaymentMethod,
  loading = false,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Don't show banner for active/trialing subscriptions or if dismissed
  if (status === "active" || status === "trialing" || dismissed) {
    return null;
  }

  // Don't show banner for free plan (nothing to warn about)
  if (plan === "free") {
    return null;
  }

  const getBannerConfig = () => {
    switch (status) {
      case "incomplete":
        return {
          icon: AlertTriangle,
          bgColor: "bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50",
          borderColor: "border-yellow-400",
          iconColor: "text-warning-400",
          textColor: "text-warning-800 dark:text-warning-200",
          descColor: "text-warning-700 dark:text-warning-300",
          title: "Payment Required",
          description:
            "Your subscription upgrade is pending payment. Complete payment now to activate premium features.",
          actionText: "Complete Payment",
          onAction: onCompletePayment,
          dismissible: false, // Don't allow dismissing incomplete status
          urgent: true,
        };

      case "past_due":
        return {
          icon: XCircle,
          bgColor: "bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50",
          borderColor: "border-red-400",
          iconColor: "text-error-400",
          textColor: "text-error-800 dark:text-error-200",
          descColor: "text-error-700 dark:text-error-300",
          title: "Payment Failed",
          description:
            "Your payment method was declined. Update your payment method to avoid service interruption.",
          actionText: "Update Payment Method",
          onAction: onUpdatePaymentMethod,
          dismissible: false,
          urgent: true,
        };

      case "canceled":
        return {
          icon: Clock,
          bgColor: "bg-gray-50 dark:bg-gray-800/60",
          borderColor: "border-gray-400 dark:border-gray-600",
          iconColor: "text-gray-400",
          textColor: "text-gray-800 dark:text-gray-200",
          descColor: "text-gray-700 dark:text-gray-300",
          title: "Subscription Canceled",
          description:
            "Your premium subscription has been canceled. Resubscribe to regain access to premium features.",
          actionText: "Resubscribe",
          onAction: onCompletePayment,
          dismissible: true,
          urgent: false,
        };

      default:
        return null;
    }
  };

  const config = getBannerConfig();

  if (!config) {
    return null;
  }

  const Icon = config.icon;

  return (
    <div
      className={`${config.bgColor} border-l-4 ${config.borderColor} p-4 rounded-r-lg shadow-md mb-6`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${config.iconColor}`} aria-hidden="true" />
        </div>
        
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-semibold ${config.textColor}`}>
            {config.title}
            {config.urgent && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200">
                Urgent
              </span>
            )}
          </h3>
          
          <p className={`mt-2 text-sm ${config.descColor}`}>
            {config.description}
          </p>

          <div className="mt-4 flex items-center space-x-3">
            {config.onAction && (
              <button
                onClick={config.onAction}
                disabled={loading}
                className={`
                  px-4 py-2 rounded-md font-medium text-sm 
                  transition-all duration-200 
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${
                    status === "incomplete" || status === "past_due"
                      ? "bg-error-600 hover:bg-error-700 text-white focus:ring-red-500"
                      : "bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500"
                  }
                `}
                aria-label={config.actionText}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  config.actionText
                )}
              </button>
            )}

            {config.dismissible && !loading && (
              <button
                onClick={() => setDismissed(true)}
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 underline"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>

        {config.dismissible && (
          <button
            onClick={() => setDismissed(true)}
            className={`ml-3 flex-shrink-0 ${config.textColor} hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded`}
            aria-label="Dismiss"
          >
            <span className="sr-only">Dismiss</span>
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
